import React, { useState, useEffect, useRef } from "react";
import "./NarrativeTooltip.css";

const NarrativeTooltip = ({ children, text, disabled = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef(null);
  const tooltipRef = useRef(null);
  const pressTimer = useRef(null);

  // Handle mouse events for desktop
  const handleMouseEnter = () => {
    if (disabled) return;
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  // Handle touch events for mobile (press and hold)
  const handleTouchStart = (e) => {
    if (disabled) return;
    pressTimer.current = setTimeout(() => {
      setIsVisible(true);
    }, 500); // 500ms press and hold
  };

  const handleTouchEnd = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
    }
  };

  const handleTouchMove = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
    }
  };

  // Handle clicking outside to close tooltip on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        isVisible
      ) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener("touchstart", handleClickOutside);
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isVisible]);

  // Position tooltip to overlay the container with same width but allow height expansion
  useEffect(() => {
    if (isVisible && containerRef.current && tooltipRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();

      // Position tooltip exactly over the container (relative to container)
      const top = 0;
      const left = 0;
      const width = containerRect.width;

      // Set the tooltip width to match container, but let height expand naturally
      tooltipRef.current.style.width = `${width}px`;
      tooltipRef.current.style.height = "auto";

      setPosition({ top, left });
    }
  }, [isVisible, text]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (pressTimer.current) {
        clearTimeout(pressTimer.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`narrative-tooltip-container ${
        isVisible ? "tooltip-active" : ""
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
    >
      {children}
      {isVisible && (
        <div
          ref={tooltipRef}
          className="narrative-tooltip"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
          }}
        >
          {text}
        </div>
      )}
    </div>
  );
};

export default NarrativeTooltip;
